import { createFileRoute } from "@tanstack/react-router";
import { z } from "zod";
import { i18nLinks, i18nOgMeta, localeUrl } from "@/lib/seo";
import {
  racesQueryOptions,
  seasonsQueryOptions,
  teamCalculatorQueryOptions,
} from "@/tools/f1-fantasy/hooks/use-f1-data";
import { getNextOrLatestRace } from "@/tools/f1-fantasy/lib/race-utils";
import { TeamCalculator } from "@/tools/f1-fantasy/pages/TeamCalculator";

// URL search params — persisted across reloads
const apexTeamSearchSchema = z.object({
  season: z.coerce.number().optional(),
  race: z.string().optional(),
  stage: z.string().optional(),
  budget: z.coerce.number().optional(),
});

export const Route = createFileRoute("/f1-fantasy/apex-team")({
  validateSearch: apexTeamSearchSchema,
  head: () => ({
    meta: [
      { title: "F1 Fantasy Apex Team — AI Optimal Teams | Toolverse" },
      {
        name: "description",
        content:
          "Build the perfect F1 Fantasy team with AI-powered optimization. Compare budget allocations, predict price changes, and maximize your fantasy points.",
      },
      {
        property: "og:title",
        content: "F1 Fantasy Apex Team — AI Optimal Teams | Toolverse",
      },
      {
        property: "og:description",
        content:
          "AI-powered F1 Fantasy team optimizer. Find the best driver and constructor combinations within your budget.",
      },
      ...i18nOgMeta("/f1-fantasy/apex-team"),
    ],
    links: [...i18nLinks("/f1-fantasy/apex-team")],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            {
              "@type": "ListItem",
              position: 1,
              name: "Home",
              item: localeUrl("/"),
            },
            {
              "@type": "ListItem",
              position: 2,
              name: "F1 Fantasy",
              item: localeUrl("/f1-fantasy"),
            },
            {
              "@type": "ListItem",
              position: 3,
              name: "Apex Team",
              item: localeUrl("/f1-fantasy/apex-team"),
            },
          ],
        }),
      },
    ],
  }),
  component: TeamCalculator,
  ssr: true,
  loaderDeps: ({ search }) => ({
    season: search.season,
    race: search.race,
  }),
  loader: async ({ context, deps }) => {
    const { season: searchSeason, race: searchRace } = deps;

    const seasons = await context.queryClient.ensureQueryData(
      seasonsQueryOptions(),
    );
    const currentSeason = searchSeason ?? seasons?.[0]?.year ?? 2026;

    const races = await context.queryClient.ensureQueryData(
      racesQueryOptions(currentSeason),
    );

    const defaultRace = getNextOrLatestRace(races ?? []);
    const targetRace = searchRace
      ? (races?.find((r) => r.slug === searchRace) ?? defaultRace)
      : defaultRace;

    // Await team calculator data so the server renders actual content (not a skeleton).
    // prefetchQuery (non-blocking) caused a hydration mismatch: server rendered skeleton
    // but client saw the data in the dehydrated cache and rendered actual content.
    // ensureQueryData blocks SSR until data is ready — server and client both render the
    // same content, eliminating the React #418 hydration error.
    if (targetRace?.slug) {
      await context.queryClient.ensureQueryData(
        teamCalculatorQueryOptions(
          currentSeason,
          targetRace.slug,
          "pre_weekend",
        ),
      );
    }

    return {
      season: currentSeason,
      raceRound: targetRace?.round ?? 0,
    };
  },
});
